import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, Scale, ShieldCheck, Mail } from "lucide-react";

/**
 * TermsOfService — plain-English legal terms tailored to Servrevu's actual
 * features (two-way ratings, Stripe subscriptions, loyalty check-ins,
 * promotional emails, referrals). Structured similarly to Yelp's linked
 * privacy page but scoped to what Servrevu actually does.
 *
 * NOT legal advice — this is the operator's declared terms. A lawyer should
 * review before scale.
 */
const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of terms",
    body: (
      <>
        <p>
          By creating an account on <strong>Servrevu</strong> (accessed at
          servrevu.com or any successor domain) or by using the site as an
          anonymous visitor, you agree to these Terms of Service and the
          separate <Link to="/privacy" className="text-[#B31E1E] hover:underline">Customer Privacy Commitment</Link>.
        </p>
        <p className="mt-2">
          If you do not agree, do not use the platform. You must be at least
          <strong> 18 years old</strong> to register.
        </p>
      </>
    ),
  },
  {
    id: "who-we-are",
    title: "2. Who we are",
    body: (
      <>
        <p>
          Servrevu is a two-way marketing ledger. It lets consumers
          (&quot;<strong>Customers</strong>&quot;) discover, review, and receive
          promotions from local businesses, and it lets those businesses
          (&quot;<strong>Businesses</strong>&quot;) rate customers, run loyalty
          programs, and send targeted marketing messages under a paid Pro
          subscription. All parties operate under a single account model with
          role-based access (customer, business, admin).
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    title: "3. Your account",
    body: (
      <>
        <p>You are responsible for:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Providing accurate signup information — email, name, address (customer) or business name and address (business).</li>
          <li>Keeping your password secure. We hash passwords server-side and never store them in cleartext.</li>
          <li>Every action taken through your account, including reviews you author and messages you send.</li>
        </ul>
        <p className="mt-2">
          You may delete your account at any time. Deletion is permanent and
          removes your reviews and notifications; loyalty and redemption logs
          retained by businesses you visited may persist in their records.
        </p>
      </>
    ),
  },
  {
    id: "customer-conduct",
    title: "4. Customer conduct",
    body: (
      <>
        <p>As a Customer you agree to:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Post only honest reviews of businesses you have personally interacted with.</li>
          <li>Never post reviews on behalf of a competitor or for pay.</li>
          <li>Refrain from harassment, hate speech, discriminatory content, personal contact information of others, or content that violates law.</li>
          <li>Not attempt to circumvent our privacy protections (e.g., scraping addresses, running bots).</li>
        </ul>
      </>
    ),
  },
  {
    id: "business-conduct",
    title: "5. Business conduct",
    body: (
      <>
        <p>As a Business you agree to:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Only rate customers who visited your establishment or transacted with your business. Fabricated reviews are grounds for removal and suspension.</li>
          <li>Send promotional messages only to customers who have opted in and only within the category preferences they have set.</li>
          <li>Not attempt to derive a customer&apos;s full street address, phone, or personal contact address from the platform — this data is intentionally masked. Circumventing masking is a violation.</li>
          <li>Honor coupon and loyalty commitments displayed in your promotions and stamp card.</li>
          <li>Comply with all applicable local laws for your line of business (health permits, alcohol service, professional licensing, etc.).</li>
        </ul>
      </>
    ),
  },
  {
    id: "reviews",
    title: "6. Reviews, disputes & moderation",
    body: (
      <>
        <p>
          Reviews are user-generated content. Servrevu does not endorse or
          verify any individual review. We reserve the right to remove or
          soft-hide reviews that violate these Terms, applicable law, or a
          successful dispute.
        </p>
        <p className="mt-2">
          Either party may dispute a review through the built-in dispute
          workflow. Disputes are visible on the review. Moderation decisions
          are recorded with an audit trail (`admin_edited_at`, `admin_edited_by`).
        </p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "7. Paid business subscriptions",
    body: (
      <>
        <p>
          Businesses may subscribe to a Servrevu <strong>Pro</strong> plan and
          optional add-ons (Featured Listing, Marketing) through Stripe.
          Subscriptions are billed on a recurring basis at the price posted at
          checkout. Current tiers:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Core Pro — access to customer directory, reviews on customers, dispute tools.</li>
          <li>Marketing add-on — audience filtering, geo- and rating-targeted message blasts, redemption tracking, drag-and-drop message builder.</li>
          <li>Featured Listing — placement boost in the directory for a fixed term.</li>
        </ul>
        <p className="mt-2">
          You may cancel at any time from your dashboard. Cancellation takes
          effect at the end of the current billing period; no refunds are
          issued for partial periods. Chargebacks without prior cancellation
          may result in permanent suspension.
        </p>
      </>
    ),
  },
  {
    id: "loyalty",
    title: "8. Loyalty check-ins & GPS",
    body: (
      <>
        <p>
          Loyalty stamp-card check-ins require the customer&apos;s device
          location to verify proximity to the business. Coarse coordinates are
          used only for that verification and are not shared with the business
          in raw form. Photos uploaded with a check-in appear on the business
          public profile with a masked author name unless the business owner
          has opted out of the public feed.
        </p>
        <p className="mt-2">
          Rewards are redeemed at the business&apos;s discretion. Servrevu
          records the redemption for both parties&apos; audit logs but is not
          a party to the underlying transaction.
        </p>
      </>
    ),
  },
  {
    id: "promotions",
    title: "9. Promotional messages",
    body: (
      <>
        <p>
          Businesses on the Marketing tier can send promotional messages to
          opted-in customers. Every message flows through our Resend pipeline;
          the business never receives the customer&apos;s contact address.
          Recipients respect the customer&apos;s master opt-in switch, per-category
          preferences, and any per-business mute. Every message includes a
          one-click unsubscribe link that mutes the sending business
          immediately.
        </p>
      </>
    ),
  },
  {
    id: "referrals",
    title: "10. Referrals & incentives",
    body: (
      <>
        <p>
          Customers may earn rewards for successful referrals as defined by
          each business&apos;s referral program. Terms of each program are
          controlled by the offering business. Servrevu tracks the referral
          code lifecycle (issued, redeemed, expired) but does not adjudicate
          the underlying reward.
        </p>
      </>
    ),
  },
  {
    id: "content-license",
    title: "11. Your content, our license",
    body: (
      <>
        <p>
          You retain ownership of everything you post — reviews, photos, replies.
          By posting, you grant Servrevu a worldwide, royalty-free, non-exclusive
          license to display, distribute, and reformat your content on the platform
          and in derivative products (mobile apps, embeddable widgets, aggregated
          analytics with names masked).
        </p>
      </>
    ),
  },
  {
    id: "prohibited",
    title: "12. Prohibited uses",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1">
          <li>Scraping, crawling, or automated data collection outside of published APIs.</li>
          <li>Impersonating another person or business.</li>
          <li>Reverse-engineering the platform to defeat masking, cooldown, or rate limits.</li>
          <li>Uploading malware, phishing content, or CSAM.</li>
          <li>Reselling access to another party&apos;s account.</li>
        </ul>
      </>
    ),
  },
  {
    id: "suspension",
    title: "13. Suspension & termination",
    body: (
      <>
        <p>
          We may suspend or terminate an account at any time for violation of
          these Terms, at our sole discretion. Paid subscribers whose accounts
          are terminated for cause are not entitled to a refund. Customers
          removed for violations forfeit any pending referral rewards.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "14. Privacy",
    body: (
      <>
        <p>
          Our privacy practices are documented in the{" "}
          <Link to="/privacy" className="text-[#B31E1E] hover:underline">Customer Privacy Commitment</Link>.
          In summary: exact street addresses, full names, email addresses, and
          phone numbers are never shared with businesses. Businesses see
          masked, minimally-scoped views only.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "15. Disclaimers & limitation of liability",
    body: (
      <>
        <p>
          Servrevu is provided <strong>&quot;as is&quot;</strong> without
          warranty of any kind, express or implied. We do not guarantee
          uninterrupted service, error-free operation, or the accuracy of any
          user-generated content. To the maximum extent permitted by law, our
          total liability to you for any claim arising from these Terms is
          limited to the amount you paid Servrevu in the twelve months
          preceding the claim, or $100, whichever is greater.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "16. Changes to these terms",
    body: (
      <>
        <p>
          We may update these Terms occasionally. Material changes will be
          announced via message or dashboard banner at least 14 days before they
          take effect. Continued use of the platform after the effective date
          constitutes acceptance.
        </p>
      </>
    ),
  },
  {
    id: "governing",
    title: "17. Governing law & disputes",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of California,
          United States, without regard to conflict-of-law rules. Any dispute
          not resolved through good-faith negotiation shall be brought
          exclusively before the state or federal courts located in San
          Francisco County, California. You waive any right to a class action.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "18. Contact",
    body: (
      <>
        <p>
          Questions, subpoenas, or legal notices should be directed to
          {" "}<a href="mailto:support@servrevu.com" className="text-[#B31E1E] hover:underline">support@servrevu.com</a>.
        </p>
      </>
    ),
  },
];

export default function TermsOfService() {
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-500 hover:text-[#B31E1E] mb-6"
        data-testid="tos-back-link"
      >
        <ArrowLeft size={13} /> Back to Servrevu
      </Link>

      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-full bg-[#B31E1E]/10 text-[#B31E1E] flex items-center justify-center shrink-0">
          <Scale size={26} />
        </div>
        <div className="min-w-0">
          <div className="overline text-[#B31E1E]">Legal · Terms of Service</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2 leading-[1.05]">
            The rules of using Servrevu.
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl leading-relaxed">
            Plain-English terms that govern how customers, businesses, and
            Servrevu itself behave on the platform. Read alongside our{" "}
            <Link to="/privacy" className="text-[#B31E1E] hover:underline">Customer Privacy Commitment</Link>.
          </p>
          <p className="mt-2 text-xs font-mono text-gray-400" data-testid="tos-effective-date">
            Effective {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </div>

      <nav aria-label="Table of contents" className="mt-10 rounded-md border border-border bg-gray-50/40 p-4" data-testid="tos-toc">
        <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
          <ScrollText size={12} /> Contents
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-gray-700 hover:text-[#B31E1E] hover:underline">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-10 space-y-10">
        {SECTIONS.map((s) => (
          <section id={s.id} key={s.id} className="scroll-mt-6" data-testid={`tos-section-${s.id}`}>
            <h2 className="font-display text-2xl">{s.title}</h2>
            <div className="mt-3 text-gray-700 leading-relaxed max-w-2xl text-sm">
              {s.body}
            </div>
          </section>
        ))}
      </div>

      <section className="mt-16 rounded-lg border border-[#B31E1E]/20 bg-[#B31E1E]/5 p-5" data-testid="tos-footer-summary">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="text-[#B31E1E] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl text-gray-900">TL;DR</h3>
            <p className="mt-1 text-sm text-gray-700 leading-relaxed max-w-2xl">
              Be honest. Don&apos;t harvest data. Pay for what you subscribe to.
              Customers keep their PII, businesses keep their money, everyone
              gets a fair rating. Contact{" "}
              <a href="mailto:support@servrevu.com" className="text-[#B31E1E] hover:underline inline-flex items-center gap-1">
                <Mail size={12} /> support@servrevu.com
              </a>{" "}
              with any questions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
